'use client';

import classnames from 'classnames';
import Box from '../../Box/Box';
import Typography from '../../Typography/Typography';
import DocumentCard from '../DocumentCard/DocumentCard';
import HiddenFileInput from '../HiddenFileInput/HiddenFileInput';
import styles from './Dropzone.module.scss';
import type { Theme, UploadedFile, InitialFile } from '../types';

export type DropzoneProps = {
  dataTestId: string;
  theme: Theme;
  label?: string;
  helperText?: string;
  dropzoneText: string;
  required: boolean;
  disabled: boolean;
  accept: string;
  multiple: boolean;
  isDragging: boolean;
  files: UploadedFile[];
  initialFiles: InitialFile[];
  documentsTitle: string;
  showDocumentCheckboxes: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onRemoveFile: (index: number) => void;
  onRetryFile: (index: number) => void;
  onDocumentClick?: (fileId: string, fileName: string) => void;
  removable: boolean;
};

/**
 * Dropzone - Dropzone variant for drag-and-drop file upload
 *
 * @component
 * @param props - DropzoneProps
 * @returns Dropzone file upload UI
 */
const Dropzone = ({
  dataTestId,
  theme,
  label,
  helperText,
  dropzoneText,
  required,
  disabled,
  accept,
  multiple,
  isDragging,
  files,
  initialFiles,
  documentsTitle,
  showDocumentCheckboxes,
  fileInputRef,
  onInputChange,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onRemoveFile,
  onRetryFile,
  onDocumentClick,
  removable,
}: DropzoneProps) => {
  return (
    <div className={styles[theme]}>
      {label && (
        <Box className={styles.labelWrapper}>
          <Typography
            variant="label"
            size="small"
            theme={theme}
            color="gray-3"
            className={styles.label}
            data-testid={`${dataTestId}-label`}
          >
            {label}
            {required && '*'}
          </Typography>
        </Box>
      )}

      <div
        className={classnames(styles.dropzone, {
          [styles.dragging]: isDragging,
          [styles.disabled]: disabled,
        })}
        onDragEnter={disabled ? undefined : onDragEnter}
        onDragLeave={disabled ? undefined : onDragLeave}
        onDragOver={disabled ? undefined : onDragOver}
        onDrop={disabled ? undefined : onDrop}
        onClick={disabled ? undefined : () => fileInputRef.current?.click()}
        data-testid={`${dataTestId}-dropzone`}
        role="button"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={
          disabled
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
              }
        }
      >
        <HiddenFileInput
          dataTestId={dataTestId}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          inputRef={fileInputRef}
          onChange={onInputChange}
        />
        <Box className={styles.dropzoneContent}>
          <Typography variant="body" size="small" theme={theme} color="gray-4">
            {dropzoneText}
          </Typography>
          {helperText && (
            <Typography
              variant="body"
              size="extraSmall"
              theme={theme}
              color="gray-5"
              data-testid={`${dataTestId}-helper-text`}
            >
              {helperText}
            </Typography>
          )}
        </Box>
      </div>

      {/* Documents Available Section */}
      {files.length > 0 && (
        <Box mt={24} mb={8} data-testid={`${dataTestId}-documents-section`}>
          <Typography
            variant="subtitle"
            size="small"
            theme={theme}
            data-testid={`${dataTestId}-documents-title`}
          >
            {documentsTitle}
          </Typography>
          <Box mt={8} className={styles.documentsList} data-testid={`${dataTestId}-file-list`}>
            {files.map((uploadedFile, index) => {
              // Use the flag from the file object to determine if pre-existing
              const isPreExisting = uploadedFile.isPreExisting ?? false;

              const successIndex = files
                .filter((f) => f.status === 'success')
                .findIndex((f) => f === uploadedFile);
              const eyebrowText =
                uploadedFile.status === 'success' && successIndex >= 0
                  ? initialFiles[successIndex]?.eyebrow
                  : undefined;

              return (
                <DocumentCard
                  key={`${uploadedFile.file.name}-${index}`}
                  dataTestId={dataTestId}
                  theme={theme}
                  fileName={uploadedFile.file.name}
                  fileSize={uploadedFile.file.size}
                  status={uploadedFile.status}
                  progress={uploadedFile.progress}
                  error={uploadedFile.error}
                  eyebrow={eyebrowText}
                  showCheckbox={uploadedFile.status === 'success' && showDocumentCheckboxes}
                  onRetry={
                    uploadedFile.status === 'error' && !uploadedFile.isValidationError
                      ? () => onRetryFile(index)
                      : undefined
                  }
                  onRemove={removable ? () => onRemoveFile(index) : undefined}
                  onDownload={
                    uploadedFile.status === 'success' && uploadedFile.url
                      ? () => onDocumentClick?.(uploadedFile.url!, uploadedFile.file.name)
                      : undefined
                  }
                  isPreExisting={isPreExisting}
                  index={index}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </div>
  );
};

Dropzone.displayName = 'Dropzone';

export default Dropzone;
